'use client';
import { useEffect, useState, useCallback } from 'react';
import { UseFormReturn, FieldValues, FieldPath, Path, PathValue } from 'react-hook-form';

export type ConditionOperator =
  | 'equals'
  | 'notEquals'
  | 'contains'
  | 'notContains'
  | 'greaterThan'
  | 'lessThan'
  | 'in'
  | 'notIn'
  | 'isEmpty'
  | 'isNotEmpty';

export interface FieldCondition<TFieldValues extends FieldValues = FieldValues> {
  /**
   * The field to watch for changes
   */
  field: FieldPath<TFieldValues>;

  /**
   * The condition operator
   */
  operator: ConditionOperator;

  /**
   * The value to compare against
   */
  value?: unknown;
}

export interface ConditionalFieldConfig<TFieldValues extends FieldValues = FieldValues> {
  /**
   * The field that should be conditionally shown/hidden
   */
  targetField: FieldPath<TFieldValues>;

  /**
   * Conditions that must be met for the field to be visible
   * Multiple conditions are AND-ed together
   */
  conditions: FieldCondition<TFieldValues>[];

  /**
   * Whether to reset the field value when hidden
   * @default true
   */
  resetOnHide?: boolean;

  /**
   * Custom logic function for complex conditions
   */
  customLogic?: (values: TFieldValues) => boolean;
}

/**
 * Hook to manage conditional field visibility based on form values
 * Supports complex conditions and automatic field cleanup
 */
export function useConditionalFields<TFieldValues extends FieldValues = FieldValues>(
  form: UseFormReturn<TFieldValues>,
  configs: ConditionalFieldConfig<TFieldValues>[]
) {
  const [visibleFields, setVisibleFields] = useState<Set<string>>(new Set());

  // Evaluate a single condition
  const evaluateCondition = useCallback(
    (condition: FieldCondition<TFieldValues>, values: TFieldValues): boolean => {
      const fieldValue = values[condition.field];
      const compareValue = condition.value;

      switch (condition.operator) {
        case 'equals':
          return fieldValue === compareValue;

        case 'notEquals':
          return fieldValue !== compareValue;

        case 'contains':
          return typeof fieldValue === 'string' && fieldValue.includes(compareValue);

        case 'notContains':
          return typeof fieldValue === 'string' && !fieldValue.includes(compareValue);

        case 'greaterThan':
          return Number(fieldValue) > Number(compareValue);

        case 'lessThan':
          return Number(fieldValue) < Number(compareValue);

        case 'in':
          return Array.isArray(compareValue) && compareValue.includes(fieldValue);

        case 'notIn':
          return Array.isArray(compareValue) && !compareValue.includes(fieldValue);

        case 'isEmpty':
          return !fieldValue || (typeof fieldValue === 'string' && fieldValue.trim() === '');

        case 'isNotEmpty':
          return !!fieldValue && (typeof fieldValue !== 'string' || fieldValue.trim() !== '');

        default:
          return false;
      }
    },
    []
  );

  // Watch form values and update visibility
  useEffect(() => {
    const subscription = form.watch((values) => {
      const newVisibleFields = new Set<string>();

      configs.forEach((config) => {
        let isVisible = true;

        // Check custom logic first if provided
        if (config.customLogic) {
          isVisible = config.customLogic(values as TFieldValues);
        } else {
          // Evaluate all conditions (AND logic)
          isVisible = config.conditions.every((condition) =>
            evaluateCondition(condition, values as TFieldValues)
          );
        }

        if (isVisible) {
          newVisibleFields.add(config.targetField);
        } else if (config.resetOnHide !== false && visibleFields.has(config.targetField)) {
          // Reset field value when hiding
          form.setValue(config.targetField, '' as PathValue<TFieldValues, Path<TFieldValues>>, {
            shouldValidate: false,
            shouldDirty: false,
          });
        }
      });

      setVisibleFields(newVisibleFields);
    });

    return () => subscription.unsubscribe();
  }, [form, configs, evaluateCondition, visibleFields]);

  // Helper function to check if a field is visible
  const isFieldVisible = (fieldName: FieldPath<TFieldValues>): boolean => {
    return visibleFields.has(fieldName);
  };

  // Helper function to get all visible fields
  const getVisibleFields = (): string[] => {
    return Array.from(visibleFields);
  };

  // Helper function to show/hide a field manually
  const setFieldVisibility = (fieldName: FieldPath<TFieldValues>, visible: boolean) => {
    setVisibleFields((prev) => {
      const newSet = new Set(prev);
      if (visible) {
        newSet.add(fieldName);
      } else {
        newSet.delete(fieldName);

        // Find config for this field to check resetOnHide
        const config = configs.find((c) => c.targetField === fieldName);
        if (config?.resetOnHide !== false) {
          form.setValue(fieldName, '' as PathValue<TFieldValues, Path<TFieldValues>>, {
            shouldValidate: false,
            shouldDirty: false,
          });
        }
      }
      return newSet;
    });
  };

  return {
    isFieldVisible,
    getVisibleFields,
    setFieldVisibility,
    visibleFields: Array.from(visibleFields),
  };
}
